import React, { useContext } from 'react'
import { Toggle } from '@strata-monorepo/shared-ui/components/Toggle/Toggle'
import { type FormStepProps, OdysseyContext } from '@redventures/odyssey'
import { TextField } from '@strata-monorepo/shared-ui/components/TextField/TextField'
import useFieldSelected from '@strata-monorepo/tracking/src/hooks/useFieldSelected'
import useFieldInputted from '@strata-monorepo/tracking/src/hooks/useFieldInputted'
import { oneThroughFivePlusOptions } from '../../constants/options/oneThroughFivePlus'
import { howManyDaysAWeekOptions } from '../../constants/options/howManyDaysAWeek'
import { useStoreState } from '../../store/useStore'

interface MonthlyEnergyComponentProps {
  tracking?: {
    stepName: string
    stepNumber: number
    stepId: string
  }
}

const MonthlyEnergy: React.FC<FormStepProps> = ({ formStep }) => {
  const odysseyContext = useContext(OdysseyContext)

  const { componentProps } = formStep
  const { tracking } = componentProps as MonthlyEnergyComponentProps

  const trackFieldSelected = useFieldSelected()
  const trackFieldInputted = useFieldInputted()
  const formId = useStoreState((state) => state.form.formId)
  const formVersion = useStoreState((state) => state.form.formVersion)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)
  const formType = useStoreState((state) => state.form.formType)

  const handleFieldSelected = (field: string, value: string): void => {
    if (!tracking) {
      return
    }

    trackFieldSelected({
      formContext: {
        formName: formVersion,
        formType,
        formId,
      },
      stepContext: {
        stepName: tracking.stepName,
        stepId: tracking.stepId,
        stepNumber: currentStepIndex,
      },
      userInputField: {
        fieldName: field,
        fieldType: 'button',
        fieldValue: value,
        fieldSource: 'manual',
        autofilled: false,
      },
    })
  }

  const handleFieldInputted = (field: string, value: string): void => {
    if (!tracking) {
      return
    }

    trackFieldInputted({
      formContext: {
        formName: formVersion,
        formType,
        formId,
      },
      stepContext: {
        stepName: tracking.stepName,
        stepId: tracking.stepId,
        stepNumber: currentStepIndex,
      },
      userInputField: {
        fieldName: field,
        fieldType: 'text',
        fieldValue: value,
        fieldSource: 'manual',
        autofilled: false,
      },
    })
  }

  return (
    <div>
      <div className='strata:flex-col strata:flex strata:gap-6 strata:@qm/qc:gap-8'>
        <div>
          <Toggle
            variant='outlined'
            label='How many people live in your home?'
            required={true}
            options={oneThroughFivePlusOptions}
            values={[odysseyContext?.getValue('monthlyEnergy.familySize') as string]}
            onChange={(values: string[]) => {
              handleFieldSelected('household_size', values[0] || '')
              odysseyContext?.setValue('monthlyEnergy.familySize', values[0])
            }}
            optionClassName='strata:font-bold'
            className='strata:text-left'
          />
          <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
            {odysseyContext?.getError('monthlyEnergy.familySize')}
          </p>
        </div>
        <div>
          <Toggle
            variant='outlined'
            label='How many days a week does your home use minimum energy?'
            options={howManyDaysAWeekOptions}
            values={[odysseyContext?.getValue('monthlyEnergy.daysAWeekAtHome') as string]}
            onChange={(values: string[]) => {
              handleFieldSelected('min_usage_days', values[0] || '')
              odysseyContext?.setValue('monthlyEnergy.daysAWeekAtHome', values[0])
            }}
            optionClassName='strata:font-bold'
            className='strata:text-left'
          />
          <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
            {odysseyContext?.getError('monthlyEnergy.daysAWeekAtHome')}
          </p>
        </div>
        <div className='strata:flex strata:flex-col strata:@qm/qc:flex-row strata:@qm/qc:gap-6 strata:gap-2 strata:justify-center strata:items-center'>
          {/* Summer Temperature */}
          <div className='strata:flex-1 strata:w-full'>
            <div className='strata:relative'>
              <TextField
                id='summerTemp'
                type='number'
                label='Summer thermostat temperature'
                value={(odysseyContext?.getValue('monthlyEnergy.summerTemp') as string) || ''}
                placeholder='Enter temperature'
                onChange={(e) => {
                  odysseyContext?.setValue('monthlyEnergy.summerTemp', e.target.value)
                }}
                onBlur={(e) => {
                  handleFieldInputted('summer_thermostat_temp', e.target.value)
                }}
                className='strata:border-gray-400 strata:placeholder:text-gray-600 strata:border strata:w-full strata:px-4 strata:py-3 strata:text-body-xs strata:rounded-sm strata:[&::-webkit-outer-spin-button]:appearance-none strata:[&::-webkit-inner-spin-button]:appearance-none strata:[&::-webkit-inner-spin-button]:m-0 strata:[-moz-appearance:textfield]'
              />
              <span className='strata:absolute strata:right-4 strata:top-11 strata:transform strata:-translate-y-1/2 strata:text-md strata:font-bold strata:text-gray-700 strata:pointer-events-none'>
                °F
              </span>
            </div>
            <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
              {odysseyContext?.getError('summerTemp')}
            </p>
          </div>

          {/* Winter Temperature */}
          <div className='strata:flex-1 strata:w-full'>
            <div className='strata:relative'>
              <TextField
                id='winterTemp'
                type='number'
                label='Winter thermostat temperature'
                value={(odysseyContext?.getValue('monthlyEnergy.winterTemp') as string) || ''}
                placeholder='Enter temperature'
                onChange={(e) => {
                  odysseyContext?.setValue('monthlyEnergy.winterTemp', e.target.value)
                }}
                onBlur={(e) => {
                  handleFieldInputted('winter_thermostat_temp', e.target.value)
                }}
                className='strata:border-gray-400 strata:placeholder:text-gray-600 strata:border strata:w-full strata:px-4 strata:py-0 strata:text-body-xs strata:rounded-sm strata:[&::-webkit-outer-spin-button]:appearance-none strata:[&::-webkit-inner-spin-button]:appearance-none strata:[&::-webkit-inner-spin-button]:m-0 strata:[-moz-appearance:textfield]'
              />
              <span className='strata:absolute strata:right-4 strata:top-11 strata:transform strata:-translate-y-1/2 strata:text-md strata:font-bold strata:text-gray-700 strata:pointer-events-none'>
                °F
              </span>
            </div>
            <p className='strata:text-left strata:text-system-negative-foreground strata:text-body-xs strata:pt-0.5 strata:@qm/qc:pt-1'>
              {odysseyContext?.getError('winterTemp')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MonthlyEnergy
